/**
 * The look of the shell (I-2510): colours, type, shape and the look's design
 * axes (surface, density, actionlist, badges, page), stored under the account config key `look` and
 * resolved by master over the layer chain (platform → tenant → company →
 * appearance → account → profile). An appearance can set the form axes (key
 * `form`) and the table defaults (key `table`) too; those are keys of their own.
 *
 * Master knows the catalog schemes only by id (`source_scheme`); their seeds,
 * pinned tokens, type and axes live in `@blpde/nx-tokens`. This module turns
 * master's answer into the complete look the shell applies:
 *
 * - A stored appearance (a numeric id) carries its complete look — forks and
 *   imports write it in full, like the layouts before them. Master's
 *   effective document IS the look; `source_scheme` only says where it came
 *   from (and keeps a scheme's hand-made token maps while the seeds match).
 * - A built-in appearance is a catalog scheme: its synthetic layer is just
 *   `{source_scheme}`. The scheme then acts as the appearance's layer — it
 *   beats what platform, tenant and company set (the platform default's type
 *   must not flatten a gallery scheme's typeface) and yields to what the
 *   account, profile or app layer set, and to every field a tenant or company
 *   locked.
 */
import {
  DEFAULT_RUNTIME_TYPE,
  PLATFORM_APPEARANCE_DEFAULT_SCHEME,
  compactTokenOverrides,
  getAppearanceScheme,
  parseRuntimeBrandSeeds,
  parseRuntimeTypeConfig,
  parseSourceScheme,
  resolveSourceScheme,
  type AppearanceLayout,
  type AppearanceTokenOverrides,
  type NamedBrand,
  type RuntimeBrandSeeds,
  type RuntimeTypeConfig,
} from "@blpde/nx-tokens";
import {
  appearanceLayerDocument,
  appearanceLayerTarget,
  cloneJson,
  isJsonObject,
  mergeJson,
  type AccountConfigLayer,
  type EnforcedField,
  type JsonObject,
} from "@blpde/connect";
import { designAxes } from "@blpde/nx-ui";
import {
  lookDesignOf,
  namedFormAxes,
  type FormState,
  type LookDesign,
  type TableZebra,
} from "@/design";

export const LOOK_CONFIG_KEY = "look";
export const APPEARANCE_CONFIG_KEY = "appearance";
/** The form axes (input, reveal, inputicon, savestatus): `design.ts` reads them. */
export const FORM_CONFIG_KEY = "form";
/**
 * Look axes whose absence is their default (master leaves them unset): a
 * look stores them only while they differ from the kit's default, so a look
 * written before such an axis existed reads — and writes back — unchanged.
 */
const UNSET_LOOK_AXES = ["actionlist", "badges", "page"] as const;
/** The table defaults (zebra, lines, …): NxTable reads them; „Tabellen" edits them. */
export const TABLE_CONFIG_KEY = "table";
export const DEFAULT_APPEARANCE_ID: NamedBrand =
  PLATFORM_APPEARANCE_DEFAULT_SCHEME;

/** What an exchange file keeps of an appearance's `table`: the zebra. */
export type TableLook = { zebra: TableZebra };

/** The zebra of the key `table`: on unless it says `off`. */
export function tableLookOf(raw: unknown): TableLook {
  const zebra = isJsonObject(raw) ? raw.zebra : undefined;
  return { zebra: zebra === "off" ? "off" : "on" };
}

/** The stored `look` document of one layer (all members optional). */
export type LookConfig = {
  source_scheme?: string;
  seeds?: Partial<RuntimeBrandSeeds>;
  tokens?: AppearanceTokenOverrides;
  type?: Partial<RuntimeTypeConfig>;
  design?: Partial<Record<string, string>>;
};

/** The `appearance` key: which appearance is active, and whether personal ones may be made. */
export type AppearanceSelection = {
  active?: string;
  allow_personal?: boolean;
};

/** A resolved, complete look — what the shell and the look designer's preview paint. */
export type AppearanceLook = {
  /** The appearance the look belongs to: a built-in's catalog id or a stored appearance's id. */
  id: string;
  /** The catalog scheme it is based on (`source_scheme`). */
  scheme: NamedBrand;
  seeds: RuntimeBrandSeeds;
  tokens?: AppearanceTokenOverrides;
  /** Set while the seeds equal the scheme's: its hand-made token maps apply. */
  sourceScheme?: NamedBrand;
  type: RuntimeTypeConfig;
  /** The look's design axes (surface, density). */
  design: LookDesign;
};

/** Layers that come at or after the appearance in the chain. */
const LATE_LAYERS: ReadonlySet<string> = new Set([
  "appearance",
  "account",
  "account_profile",
  "account_application",
  "account_device",
]);

/** A built-in appearance id is a catalog id; stored appearances have numeric ids. */
export function isBuiltinAppearanceId(id: string): boolean {
  return !/^\d+$/.test(id);
}

/** The catalog scheme a stored `source_scheme` names, else the platform default. */
export function schemeOf(raw: unknown): NamedBrand {
  return parseSourceScheme(raw) ?? DEFAULT_APPEARANCE_ID;
}

/**
 * A catalog scheme as a look layer: its seeds (an `info` that only mirrors
 * the brand is left out, so it keeps following the brand), its pinned colours
 * and shared tokens, and its own type and axes where it has them.
 */
export function catalogLookLayer(id: NamedBrand): LookConfig {
  const scheme =
    getAppearanceScheme(id) ?? getAppearanceScheme(DEFAULT_APPEARANCE_ID)!;
  const { info, ...seeds } = scheme.seeds;
  const tokens = compactTokenOverrides({
    ...scheme.overrides,
    shared: scheme.shared,
  });
  return {
    source_scheme: scheme.id,
    seeds:
      info && info.toLowerCase() !== scheme.seeds.brand.toLowerCase()
        ? { ...seeds, info }
        : seeds,
    ...(tokens ? { tokens } : {}),
    ...(scheme.type
      ? {
          type: {
            ...scheme.type,
            ...(scheme.type.scopes
              ? { scopes: { ...scheme.type.scopes } }
              : {}),
          },
        }
      : {}),
    ...(scheme.design ? { design: { ...scheme.design } } : {}),
  };
}

/** `layer` without every member `over` sets (member by member for objects). */
function withoutPaths(layer: JsonObject, over: JsonObject): JsonObject {
  const out: JsonObject = {};
  for (const [key, value] of Object.entries(layer)) {
    if (!(key in over) || over[key] === undefined) {
      out[key] = value;
      continue;
    }
    if (isJsonObject(value) && isJsonObject(over[key])) {
      const nested = withoutPaths(value, over[key] as JsonObject);
      if (Object.keys(nested).length) out[key] = nested;
    }
  }
  return out;
}

function lateData(
  layers: readonly AccountConfigLayer[] | null | undefined,
): JsonObject {
  let merged: JsonObject = {};
  for (const layer of layers ?? []) {
    if (!LATE_LAYERS.has(layer.subjectType)) continue;
    merged = mergeJson(merged, layer.data) as JsonObject;
  }
  return merged;
}

/**
 * The stored data of the appearance's own layer, when the layers hold it:
 * master names the appearance of that layer (`appearance`); without the name
 * a stored appearance is matched by its id. Null when master resolved another
 * appearance (one the session may not see falls back to the default).
 */
export function appearanceLayerData(
  layers: readonly AccountConfigLayer[] | null | undefined,
  id?: string,
): LookConfig | null {
  const numeric = id && !isBuiltinAppearanceId(id) ? Number(id) : null;
  const layer = (layers ?? []).find((item) => {
    if (item.subjectType !== "appearance") return false;
    if (item.appearance && id) return item.appearance === id;
    return numeric == null || item.subjectId === numeric;
  });
  return layer ? (cloneJson(layer.data) as LookConfig) : null;
}

function hasCompleteSeeds(raw: unknown): boolean {
  return parseRuntimeBrandSeeds(raw) != null;
}

export type ResolveLookInput = {
  /** The appearance whose look this is (active, or previewed). */
  id: string;
  /** Master's effective `look`. */
  effective: LookConfig | JsonObject;
  /** The stored layers (`?layers=1`), when master sent them. */
  layers?: readonly AccountConfigLayer[] | null;
  /** Fields a tenant or company locked. */
  enforced?: readonly EnforcedField[];
};

/**
 * The complete look for master's answer. See the module comment for why a
 * built-in's scheme sits at the appearance's place in the chain.
 *
 * That place needs the layers: only they tell which fields the account, the
 * profile or the app set. Without them (an answer without layers, a first
 * start without a boot cache) the scheme only fills what the effective
 * document leaves open — it never covers a setting of the user.
 */
export function resolveLook(input: ResolveLookInput): AppearanceLook {
  const effective = (
    isJsonObject(input.effective) ? input.effective : {}
  ) as JsonObject;
  const scheme = schemeOf(effective.source_scheme);
  const own = appearanceLayerData(input.layers, input.id);
  const complete = own
    ? hasCompleteSeeds(own.seeds)
    : !isBuiltinAppearanceId(input.id) && hasCompleteSeeds(effective.seeds);

  let merged: JsonObject = effective;
  if (!complete) {
    const locked = new Set((input.enforced ?? []).map((item) => item.field));
    const layer = catalogLookLayer(scheme) as JsonObject;
    for (const field of locked) delete layer[field];
    merged = (
      input.layers
        ? mergeJson(effective, withoutPaths(layer, lateData(input.layers)))
        : mergeJson(layer, effective)
    ) as JsonObject;
  }
  return lookFromConfig(input.id, merged as LookConfig, scheme);
}

/**
 * The look of an appearance itself — as everybody without a value of their
 * own sees it: the layers up to and including the appearance's (platform,
 * tenant, company, the appearance), never the account's, the profile's or an
 * app's. What the look designer shows and writes back onto the appearance's
 * layer: a personal value of the editor must not travel into a shared design.
 * Without the layers (master did not send them) the effective document is all
 * there is.
 */
export function resolveAppearanceLook(input: ResolveLookInput): AppearanceLook {
  if (!input.layers) return resolveLook(input);
  const layers = input.layers.filter(
    (layer) =>
      layer.subjectType === "appearance" || !LATE_LAYERS.has(layer.subjectType),
  );
  const effective =
    appearanceLayerDocument(appearanceLayerTarget(input.id), {
      layers,
      enforced: input.enforced,
    }) ?? {};
  return resolveLook({ ...input, effective, layers });
}

/** Parses a (merged) look document into a complete look, filling gaps from the scheme and the defaults. */
export function lookFromConfig(
  id: string,
  config: LookConfig,
  scheme: NamedBrand = schemeOf(config.source_scheme),
): AppearanceLook {
  const catalog = catalogLookLayer(scheme);
  const seeds =
    parseRuntimeBrandSeeds(config.seeds) ??
    parseRuntimeBrandSeeds(mergeJson(catalog.seeds, config.seeds ?? {})) ??
    (catalog.seeds as RuntimeBrandSeeds);
  const tokens = compactTokenOverrides(config.tokens);
  const type = parseRuntimeTypeConfig(
    mergeJson(DEFAULT_RUNTIME_TYPE, config.type ?? {}),
  ) ?? { ...DEFAULT_RUNTIME_TYPE };
  const sourceScheme = resolveSourceScheme(seeds, scheme);
  return {
    id,
    scheme,
    seeds: { ...seeds },
    ...(tokens ? { tokens } : {}),
    ...(sourceScheme ? { sourceScheme } : {}),
    type,
    // Only the look's own axes: form axes a layer still holds are ignored.
    design: lookDesignOf(config.design),
  };
}

/**
 * The complete, self-contained `look` document of a stored appearance: what a
 * fork or an import writes, so the appearance keeps its look whatever the
 * layers below it change.
 */
export function materializeLook(look: AppearanceLook): LookConfig {
  const type: RuntimeTypeConfig = {
    ...look.type,
    ...(look.type.scopes ? { scopes: { ...look.type.scopes } } : {}),
  };
  return {
    source_scheme: look.scheme,
    seeds: { ...look.seeds },
    ...(look.tokens ? { tokens: cloneJson(look.tokens) } : {}),
    type,
    design: storedLookDesign(look.design),
  };
}

/** The look's axes as master keeps them: the unset-by-default ones only when set. */
function storedLookDesign(design: LookDesign): Record<string, string> {
  const out: Record<string, string> = { ...design };
  for (const axis of UNSET_LOOK_AXES) {
    if (out[axis] === designAxes[axis].default) delete out[axis];
  }
  return out;
}

/**
 * The look of an exchange-file layout (`formica.appearance.layout`), for an
 * import. A layout without type or axes takes them from `base`, as it did
 * when it was a local layout. The file's form axes and table zebra are read
 * by `formFromLayout` / `tableFromLayout`.
 */
export function lookFromLayout(
  layout: AppearanceLayout,
  base: AppearanceLook,
): AppearanceLook {
  const scheme = layout.sourceScheme ?? DEFAULT_APPEARANCE_ID;
  const tokens = compactTokenOverrides(layout.tokens);
  return {
    id: layout.id,
    scheme,
    seeds: { ...layout.seeds },
    ...(tokens ? { tokens } : {}),
    ...(resolveSourceScheme(layout.seeds, scheme)
      ? { sourceScheme: scheme }
      : {}),
    type: layout.type
      ? {
          ...layout.type,
          ...(layout.type.scopes ? { scopes: { ...layout.type.scopes } } : {}),
        }
      : { ...base.type },
    design: lookDesignOf(layout.design, base.design),
  };
}

/**
 * The form axes an exchange-file layout names (its `design` holds every axis,
 * as the file format always did) — only the ones it sets, for the `form`
 * layer of the imported appearance.
 */
export function formFromLayout(layout: AppearanceLayout): Partial<FormState> {
  return namedFormAxes(layout.design);
}

/** The table zebra an exchange-file layout names (`design.zebra`), if any. */
export function tableFromLayout(layout: AppearanceLayout): Partial<TableLook> {
  const zebra = (
    layout.design as Record<string, string | undefined> | undefined
  )?.zebra;
  return zebra === "on" || zebra === "off" ? { zebra } : {};
}

/**
 * An edit of an appearance in the look designer (what the inspector
 * changes): the look's parts, and merge patches of the appearance's layer of
 * any other key — every row of its settings list („Formulare", key `form`;
 * „Tabellen", key `table`; …) — per key in the order they were made.
 */
export type LookEdit = {
  seeds?: RuntimeBrandSeeds;
  /** `null` removes every token override. */
  tokens?: AppearanceTokenOverrides | null;
  type?: RuntimeTypeConfig;
  design?: Partial<LookDesign>;
  /** Merge patches of other keys' layers of the appearance, per key, oldest first. */
  settings?: Record<string, JsonObject[]>;
};

function mergeSettings(
  first: LookEdit["settings"],
  second: LookEdit["settings"],
): LookEdit["settings"] {
  if (!first) return second;
  if (!second) return first;
  const out: Record<string, JsonObject[]> = { ...first };
  for (const [key, patches] of Object.entries(second))
    out[key] = [...(out[key] ?? []), ...patches];
  return out;
}

/**
 * Two edits as one: seeds, tokens and type replace; design axes merge; the
 * other keys' patches of the second follow the first's.
 */
export function mergeLookEdits(
  first: LookEdit | null,
  second: LookEdit | null,
): LookEdit | null {
  if (!first) return second;
  if (!second) return first;
  return {
    ...first,
    ...(second.seeds ? { seeds: second.seeds } : {}),
    ...(second.tokens !== undefined ? { tokens: second.tokens } : {}),
    ...(second.type ? { type: second.type } : {}),
    ...(second.design ? { design: { ...first.design, ...second.design } } : {}),
    ...(second.settings
      ? { settings: mergeSettings(first.settings, second.settings) }
      : {}),
  };
}

/** Whether an edit changes the look itself (not only other keys of the appearance). */
export function editsLook(edit: LookEdit | null): boolean {
  return Boolean(
    edit &&
    (edit.seeds || edit.tokens !== undefined || edit.type || edit.design),
  );
}

/** The look with an edit applied (the scheme's hand-made maps only while the seeds still match). */
export function applyLookEdit(
  look: AppearanceLook,
  edit: LookEdit | null,
): AppearanceLook {
  if (!edit) return look;
  const seeds = edit.seeds ? { ...edit.seeds } : look.seeds;
  const tokens =
    edit.tokens === undefined
      ? look.tokens
      : compactTokenOverrides(edit.tokens ?? undefined);
  const sourceScheme = resolveSourceScheme(seeds, look.scheme);
  const next: AppearanceLook = {
    id: look.id,
    scheme: look.scheme,
    seeds,
    type: edit.type ? { ...edit.type } : look.type,
    design: edit.design ? lookDesignOf(edit.design, look.design) : look.design,
  };
  if (tokens) next.tokens = tokens;
  if (sourceScheme) next.sourceScheme = sourceScheme;
  return next;
}

/**
 * An appearance as an exchange-file layout (export). The file's `design`
 * keeps every axis as the format always did: the look's, the form's and the
 * table zebra.
 */
export function layoutFromLook(
  look: AppearanceLook,
  name: string,
  id: string,
  extras: { form?: FormState; table?: TableLook } = {},
): AppearanceLayout {
  const tokens = compactTokenOverrides(look.tokens);
  return {
    id,
    name,
    seeds: { ...look.seeds },
    sourceScheme: look.scheme,
    ...(tokens ? { tokens } : {}),
    type: {
      ...look.type,
      ...(look.type.scopes ? { scopes: { ...look.type.scopes } } : {}),
    },
    design: {
      ...look.design,
      ...extras.form,
      ...(extras.table ? { zebra: extras.table.zebra } : {}),
    },
  };
}
